import React, { useEffect, useState } from 'react';
import { PlusCircle, Layers, Edit, Trash2, Power, AlertTriangle, X } from 'lucide-react';
import { Header } from '../components/Header.js';
import { EduCard } from '../components/EduCard.js';
import { EduButton } from '../components/EduButton.js';
import { EduBadge } from '../components/EduBadge.js';
import { api } from '../services/api.js';
import { Plan } from '../types/index.js';

export const Plans: React.FC = () => {
  const [plans, setPlans] = useState<Plan[]>([]);
  const [loading, setLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editingPlan, setEditingPlan] = useState<Plan | null>(null);

  // New plan form state
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [monthlyPriceReais, setMonthlyPriceReais] = useState(199);
  const [monthlyLessonLimit, setMonthlyLessonLimit] = useState(30);
  const [maxVideoDurationMinutes, setMaxVideoDurationMinutes] = useState(30);
  const [maxProjects, setMaxProjects] = useState(20);
  const [submitting, setSubmitting] = useState(false);

  // Edit plan form state
  const [editName, setEditName] = useState('');
  const [editDescription, setEditDescription] = useState('');
  const [editMonthlyPriceReais, setEditMonthlyPriceReais] = useState(199);
  const [editMonthlyLessonLimit, setEditMonthlyLessonLimit] = useState(30);
  const [editMaxVideoDurationMinutes, setEditMaxVideoDurationMinutes] = useState(30);
  const [editMaxProjects, setEditMaxProjects] = useState(20);
  const [submittingEdit, setSubmittingEdit] = useState(false);

  const loadPlans = async () => {
    try {
      setLoading(true);
      const res = await api.getPlans();
      setPlans(res.items || []);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadPlans();
  }, []);

  const handleCreatePlan = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.createPlan({
        name,
        description,
        monthlyPrice: Math.round(monthlyPriceReais * 100),
        yearlyPrice: Math.round(monthlyPriceReais * 10 * 100),
        monthlyLessonLimit,
        maxVideoDurationMinutes,
        maxProjects,
        active: true,
      });
      setIsModalOpen(false);
      setName('');
      setDescription('');
      loadPlans();
    } catch (err: any) {
      alert(err.message || 'Falha ao criar plano.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleOpenEdit = (plan: Plan) => {
    setEditingPlan(plan);
    setEditName(plan.name);
    setEditDescription(plan.description || '');
    setEditMonthlyPriceReais(plan.monthlyPrice / 100);
    setEditMonthlyLessonLimit(plan.monthlyLessonLimit);
    setEditMaxVideoDurationMinutes(plan.maxVideoDurationMinutes);
    setEditMaxProjects(plan.maxProjects || 20);
    setIsEditModalOpen(true);
  };

  const handleUpdatePlan = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingPlan) return;
    setSubmittingEdit(true);
    try {
      await api.updatePlan(editingPlan.id, {
        name: editName,
        description: editDescription,
        monthlyPrice: Math.round(editMonthlyPriceReais * 100),
        yearlyPrice: Math.round(editMonthlyPriceReais * 10 * 100),
        monthlyLessonLimit: editMonthlyLessonLimit,
        maxVideoDurationMinutes: editMaxVideoDurationMinutes,
        maxProjects: editMaxProjects,
      });
      alert('Plano atualizado com sucesso!');
      setIsEditModalOpen(false);
      setEditingPlan(null);
      loadPlans();
    } catch (err: any) {
      alert(err.message || 'Falha ao atualizar plano.');
    } finally {
      setSubmittingEdit(false);
    }
  };

  const handleToggleActive = async (plan: Plan) => {
    const actionLabel = plan.active ? 'desativar' : 'ativar';
    if (!confirm(`Deseja realmente ${actionLabel} o plano "${plan.name}"?`)) return;

    try {
      await api.updatePlan(plan.id, { active: !plan.active });
      loadPlans();
    } catch (err: any) {
      alert(err.message || `Erro ao ${actionLabel} plano.`);
    }
  };

  const handleDeletePlan = async (plan: Plan) => {
    const confirmMsg = `Deseja realmente excluir o plano "${plan.name}"?\n\nSomente é permitido excluir planos que não possuam clientes ou assinaturas vinculadas.`;
    if (!confirm(confirmMsg)) return;

    try {
      await api.deletePlan(plan.id);
      alert('Plano excluído com sucesso!');
      loadPlans();
    } catch (err: any) {
      alert(err.message || 'Falha ao excluir plano.');
    }
  };

  return (
    <div>
      <Header
        title="Planos de Assinatura"
        subtitle="Gerenciamento comercial das modalidades de contratação, limites e recursos"
        action={
          <EduButton
            variant="primary"
            size="sm"
            onClick={() => setIsModalOpen(true)}
            icon={<PlusCircle size={16} />}
          >
            Novo Plano
          </EduButton>
        }
      />

      <div className="p-8 max-w-7xl mx-auto space-y-6">
        {loading ? (
          <div className="p-12 text-center text-xs text-txt-muted">Carregando planos...</div>
        ) : plans.length === 0 ? (
          <EduCard>
            <div className="text-center py-12">
              <Layers size={36} className="mx-auto text-txt-muted mb-2" />
              <h3 className="text-sm font-semibold text-txt-primary">Nenhum plano cadastrado</h3>
            </div>
          </EduCard>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {plans.map((plan) => (
              <EduCard
                key={plan.id}
                title={
                  <div className="flex items-center justify-between w-full">
                    <span className="font-bold text-sm text-txt-primary">{plan.name}</span>
                    <EduBadge variant={plan.active ? 'success' : 'neutral'} size="sm" dot>
                      {plan.active ? 'Ativo' : 'Inativo'}
                    </EduBadge>
                  </div>
                }
                footer={
                  <div className="flex items-center justify-between w-full text-xs pt-1">
                    <span className="text-txt-muted font-mono text-[11px] truncate max-w-[90px]" title={plan.id}>
                      {plan.id}
                    </span>
                    <div className="flex items-center gap-3">
                      <button
                        onClick={() => handleToggleActive(plan)}
                        className={`font-semibold hover:underline flex items-center gap-1 ${
                          plan.active ? 'text-amber-600 hover:text-amber-700' : 'text-emerald-600 hover:text-emerald-700'
                        }`}
                        title={plan.active ? 'Desativar este plano' : 'Ativar este plano'}
                      >
                        <Power size={13} /> {plan.active ? 'Desativar' : 'Ativar'}
                      </button>
                      <button
                        onClick={() => handleOpenEdit(plan)}
                        className="text-brand-primary font-semibold hover:underline flex items-center gap-1"
                        title="Editar parâmetros do plano"
                      >
                        <Edit size={13} /> Editar
                      </button>
                      <button
                        onClick={() => handleDeletePlan(plan)}
                        className="text-status-error font-semibold hover:underline flex items-center gap-1"
                        title="Excluir plano (apenas se sem vínculos)"
                      >
                        <Trash2 size={13} /> Excluir
                      </button>
                    </div>
                  </div>
                }
              >
                <div className="mb-4">
                  <div className="flex items-baseline gap-1">
                    <span className="text-3xl font-black text-txt-primary">
                      R$ {(plan.monthlyPrice / 100).toFixed(2).replace('.', ',')}
                    </span>
                    <span className="text-xs text-txt-secondary">/ mês</span>
                  </div>
                  <p className="text-xs text-txt-secondary mt-1 min-h-[32px]">
                    {plan.description || 'Sem descrição cadastrada.'}
                  </p>
                </div>

                <div className="space-y-2 border-t border-bdr pt-4 text-xs">
                  <div className="flex justify-between">
                    <span className="text-txt-secondary">Franquia Mensal:</span>
                    <strong className="text-txt-primary">{plan.monthlyLessonLimit} aulas</strong>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-txt-secondary">Duração Máxima:</span>
                    <strong className="text-txt-primary">{plan.maxVideoDurationMinutes} min / aula</strong>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-txt-secondary">Projetos Simultâneos:</span>
                    <strong className="text-txt-primary">{plan.maxProjects || 10}</strong>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-txt-secondary">Vozes Autorizadas:</span>
                    <strong className="text-brand-primary capitalize">
                      {plan.availableVoices?.join(', ') || 'faber'}
                    </strong>
                  </div>
                </div>
              </EduCard>
            ))}
          </div>
        )}
      </div>

      {/* Modal Novo Plano */}
      {isModalOpen && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4">
          <div className="bg-surface rounded-xl border border-bdr shadow-xl max-w-md w-full p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-base font-bold text-txt-primary">Novo Plano de Assinatura</h3>
              <button onClick={() => setIsModalOpen(false)} className="text-txt-muted hover:text-txt-primary">
                <X size={18} />
              </button>
            </div>
            <form onSubmit={handleCreatePlan} className="space-y-4">
              <div>
                <label className="block text-xs font-semibold text-txt-primary mb-1">
                  Nome do Plano *
                </label>
                <input
                  type="text"
                  required
                  placeholder="Ex: Enterprise Custom"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className="w-full px-3 py-2 text-sm border border-bdr rounded-md focus:outline-none focus:border-brand-primary"
                />
              </div>

              <div>
                <label className="block text-xs font-semibold text-txt-primary mb-1">
                  Valor Mensal (R$) *
                </label>
                <input
                  type="number"
                  required
                  min={1}
                  step="0.01"
                  value={monthlyPriceReais}
                  onChange={(e) => setMonthlyPriceReais(Number(e.target.value))}
                  className="w-full px-3 py-2 text-sm border border-bdr rounded-md focus:outline-none focus:border-brand-primary"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-semibold text-txt-primary mb-1">
                    Limite de Aulas / Mês *
                  </label>
                  <input
                    type="number"
                    required
                    min={1}
                    value={monthlyLessonLimit}
                    onChange={(e) => setMonthlyLessonLimit(Number(e.target.value))}
                    className="w-full px-3 py-2 text-sm border border-bdr rounded-md focus:outline-none focus:border-brand-primary"
                  />
                </div>

                <div>
                  <label className="block text-xs font-semibold text-txt-primary mb-1">
                    Duração Máxima (Min)
                  </label>
                  <input
                    type="number"
                    required
                    min={1}
                    value={maxVideoDurationMinutes}
                    onChange={(e) => setMaxVideoDurationMinutes(Number(e.target.value))}
                    className="w-full px-3 py-2 text-sm border border-bdr rounded-md focus:outline-none focus:border-brand-primary"
                  />
                </div>
              </div>

              <div>
                <label className="block text-xs font-semibold text-txt-primary mb-1">
                  Projetos Simultâneos
                </label>
                <input
                  type="number"
                  required
                  min={1}
                  value={maxProjects}
                  onChange={(e) => setMaxProjects(Number(e.target.value))}
                  className="w-full px-3 py-2 text-sm border border-bdr rounded-md focus:outline-none focus:border-brand-primary"
                />
              </div>

              <div>
                <label className="block text-xs font-semibold text-txt-primary mb-1">
                  Descrição
                </label>
                <textarea
                  rows={2}
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder="Resumo do público-alvo deste plano..."
                  className="w-full px-3 py-2 text-sm border border-bdr rounded-md focus:outline-none focus:border-brand-primary"
                />
              </div>

              <div className="flex justify-end gap-2 pt-2">
                <EduButton
                  type="button"
                  variant="secondary"
                  size="sm"
                  onClick={() => setIsModalOpen(false)}
                >
                  Cancelar
                </EduButton>
                <EduButton
                  type="submit"
                  variant="primary"
                  size="sm"
                  isLoading={submitting}
                >
                  Salvar Plano
                </EduButton>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal Editar Plano */}
      {isEditModalOpen && editingPlan && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4">
          <div className="bg-surface rounded-xl border border-bdr shadow-xl max-w-md w-full p-6">
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-base font-bold text-txt-primary">Editar Plano: {editingPlan.name}</h3>
              <button
                onClick={() => {
                  setIsEditModalOpen(false);
                  setEditingPlan(null);
                }}
                className="text-txt-muted hover:text-txt-primary"
              >
                <X size={18} />
              </button>
            </div>

            <div className="mb-4 p-3 bg-amber-500/10 border border-amber-500/20 rounded-lg flex items-start gap-2 text-xs text-amber-700">
              <AlertTriangle size={16} className="shrink-0 mt-0.5" />
              <span>
                Regra do Sistema: Se houver clientes ou assinaturas ativas vinculadas a este plano, as alterações de limites e valores serão bloqueadas pelo servidor.
              </span>
            </div>

            <form onSubmit={handleUpdatePlan} className="space-y-4">
              <div>
                <label className="block text-xs font-semibold text-txt-primary mb-1">
                  Nome do Plano *
                </label>
                <input
                  type="text"
                  required
                  value={editName}
                  onChange={(e) => setEditName(e.target.value)}
                  className="w-full px-3 py-2 text-sm border border-bdr rounded-md focus:outline-none focus:border-brand-primary"
                />
              </div>

              <div>
                <label className="block text-xs font-semibold text-txt-primary mb-1">
                  Valor Mensal (R$) *
                </label>
                <input
                  type="number"
                  required
                  min={1}
                  step="0.01"
                  value={editMonthlyPriceReais}
                  onChange={(e) => setEditMonthlyPriceReais(Number(e.target.value))}
                  className="w-full px-3 py-2 text-sm border border-bdr rounded-md focus:outline-none focus:border-brand-primary"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-semibold text-txt-primary mb-1">
                    Limite de Aulas / Mês *
                  </label>
                  <input
                    type="number"
                    required
                    min={1}
                    value={editMonthlyLessonLimit}
                    onChange={(e) => setEditMonthlyLessonLimit(Number(e.target.value))}
                    className="w-full px-3 py-2 text-sm border border-bdr rounded-md focus:outline-none focus:border-brand-primary"
                  />
                </div>

                <div>
                  <label className="block text-xs font-semibold text-txt-primary mb-1">
                    Duração Máxima (Min)
                  </label>
                  <input
                    type="number"
                    required
                    min={1}
                    value={editMaxVideoDurationMinutes}
                    onChange={(e) => setEditMaxVideoDurationMinutes(Number(e.target.value))}
                    className="w-full px-3 py-2 text-sm border border-bdr rounded-md focus:outline-none focus:border-brand-primary"
                  />
                </div>
              </div>

              <div>
                <label className="block text-xs font-semibold text-txt-primary mb-1">
                  Projetos Simultâneos
                </label>
                <input
                  type="number"
                  required
                  min={1}
                  value={editMaxProjects}
                  onChange={(e) => setEditMaxProjects(Number(e.target.value))}
                  className="w-full px-3 py-2 text-sm border border-bdr rounded-md focus:outline-none focus:border-brand-primary"
                />
              </div>

              <div>
                <label className="block text-xs font-semibold text-txt-primary mb-1">
                  Descrição
                </label>
                <textarea
                  rows={2}
                  value={editDescription}
                  onChange={(e) => setEditDescription(e.target.value)}
                  placeholder="Resumo do público-alvo deste plano..."
                  className="w-full px-3 py-2 text-sm border border-bdr rounded-md focus:outline-none focus:border-brand-primary"
                />
              </div>

              <div className="flex justify-end gap-2 pt-2">
                <EduButton
                  type="button"
                  variant="secondary"
                  size="sm"
                  onClick={() => {
                    setIsEditModalOpen(false);
                    setEditingPlan(null);
                  }}
                >
                  Cancelar
                </EduButton>
                <EduButton
                  type="submit"
                  variant="primary"
                  size="sm"
                  isLoading={submittingEdit}
                >
                  Salvar Alterações
                </EduButton>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};
